import UnsavedReportChangesModal from './UnsavedReportChangesModal'
import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import api from '../../../services/api'
import { useAuth } from '../../../contexts/AuthContext'
import { usePageHeader } from '../../../contexts/PageHeaderContext'
import ConfirmModal from '../../../components/ConfirmModal'
import EntityComments from '../../../components/EntityComments'
import Icon from '../../../components/Icon'
import { Button } from '../../../components/ui'
import ActionMenu from '../../../components/ui/ActionMenu'
import IncomeReportActs from './IncomeReportActs'
import IncomeReportCash from './IncomeReportCash'
import { actPaymentLabel } from '../../../components/ui/ActBadges'
import { calculateIncomeReport } from './incomeReportCalculations'
import {
  BASE_COLUMNS,
  EditableReportTitle,
  ReportDataTable,
  ReportErrorState,
  ReportInfoCards,
  ReportLoadingState,
  ReportPageShell,
  ReportStatusBadge,
  ReportPnlSummary,
  reportTitle,
} from './IncomeReportComponents'
import {
  buildCsvContent,
  formatCsvMoney,
  formatCsvPercent,
} from './incomeReportCsv'
import './Reports.css'
import useIncomeReportEditor from './useIncomeReportEditor'

const GROUPING_VALUES = new Set(['none', 'team', 'type'])
const MAIN_SORT_VALUES = new Set(['team', 'name'])
const AUTO_SAVE_INTERVAL_MS = 5 * 60 * 1000

export function formatAutoSaveLabel(date) {
  if (!date) return 'Автосохранения ещё не было'
  const time = new Intl.DateTimeFormat('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
  return `Автосохранение в ${time}`
}

function parseGrouping(value) {
  return GROUPING_VALUES.has(value) ? value : 'none'
}

function parseMainSort(value) {
  return MAIN_SORT_VALUES.has(value) ? value : 'team'
}

function parseColumnSort(searchParams) {
  const column = searchParams.get('sortColumn')
  if (!BASE_COLUMNS.some((item) => item.id === column && item.sortable)) return null
  return {
    column,
    direction: searchParams.get('sortDirection') === 'desc' ? 'desc' : 'asc',
  }
}

function columnDisplayValue(row, column, index) {
  if (column.id === 'num') return Number.isInteger(index) ? index + 1 : ''
  if (column.format === 'money') return formatCsvMoney(row[column.id])
  if (column.format === 'percent') return formatCsvPercent(row[column.id])
  if (column.format === 'fractionPercent') return formatCsvPercent(row[column.id], { fraction: true })
  if (column.id === 'team_name') return row.team_name || ''
  if (column.id === 'employee_name') return row.employee_name || ''
  if (column.id === 'employee_type') return row.employee_type || ''
  return row[column.id] ?? ''
}

function emptyCsvTableRow() {
  return BASE_COLUMNS.map(() => '')
}

function totalColumnDisplayValue(totals, column, label) {
  if (column.id === 'employee_name') return label
  if (column.id === 'num' || !column.numeric) return ''
  if (column.format === 'percent') return formatCsvPercent(totals[column.id])
  if (column.format === 'fractionPercent') return formatCsvPercent(totals[column.id], { fraction: true })
  if (column.format === 'money') return formatCsvMoney(totals[column.id])
  return totals[column.id] ?? ''
}

export function buildIncomeReportCsv(result, acts = [], config = {}) {
  const header = BASE_COLUMNS.map((column) => column.label)
  const lines = [header]
  result.rows.forEach((row, index) => {
    lines.push(BASE_COLUMNS.map((column) => columnDisplayValue(row, column, index)))
  })
  const includePinnedRows = result.rows.length > 0 || Number(result.miscExpenseRow?.toReceive || 0) > 0
  if (result.rows.length > 0) lines.push(emptyCsvTableRow())
  if (includePinnedRows) {
    lines.push(BASE_COLUMNS.map((column) => columnDisplayValue(result.miscExpenseRow, column, '')))
    lines.push(BASE_COLUMNS.map((column) => totalColumnDisplayValue(result.grandTotals || result.totals || {}, column, 'ИТОГО')))
  }
  lines.push([])
  lines.push(['Паевой фонд', formatCsvMoney(result.infoCards.payFund)])
  lines.push(['Наличка (окр.) итого', formatCsvMoney(result.infoCards.cashRoundedTotal)])
  lines.push(['Баланс доп. расходов', formatCsvMoney(result.infoCards.additionalBalance)])
  if (acts?.length) {
    lines.push([], ['Акты за период'], ['Акт', 'Объект', 'Стадия', 'Дата выставления', 'Сумма', 'Тип оплаты', 'Статус', 'Распределение'])
    acts.forEach((act) => lines.push([
      act.act_number || act.act_id, act.object_name || '', act.stage_id || '',
      act.issue_date || '', formatCsvMoney(act.amount), actPaymentLabel(act), act.payment_type === 'artel' ? '' : act.status || 'Не указан',
      act.distribution_exclusion_reason || 'Участвует в распределении',
    ]))
  }
  if (result.pnl) {
    lines.push([], ['Официальный фонд'], ['Итого доходов', formatCsvMoney(result.pnl.totalIncome)],
      ['Внутренние работы за счёт артели', formatCsvMoney(result.pnl.artelExpense)],
      ['Сумма расходов', formatCsvMoney(result.pnl.totalExpensesFact)],
      ['Общая прибыль', formatCsvMoney(result.pnl.profit)],
      ['Выплаты из официального фонда', formatCsvMoney(result.pnl.profitDistribution)],
      ['Фонд содержания (конец периода)', formatCsvMoney(result.pnl.fundEnd)])
  }
  if (config.payment_summary && config.show_cash_receipts !== false) {
    lines.push([], ['Поступления наличными (вне официального отчёта)'],
      ['Поступило', formatCsvMoney(config.payment_summary.cash_income)],
      ['Выплаты по наличным актам', formatCsvMoney(config.payment_summary.cash_payouts)],
      ['Акт', 'Объект', 'Стадия', 'Поступления', 'Выплаты сотрудникам'])
    ;(acts || []).filter((act) => act.payment_type === 'cash' && act.distribution_eligible).forEach((act) =>
      lines.push([act.act_number, act.object_name || '', act.stage_id || '', formatCsvMoney(act.amount), formatCsvMoney(act.employee_payout)]))
  }
  return buildCsvContent(lines)
}

function downloadCsv(filename, content) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

function IncomeReportPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { user } = useAuth()
  const {
    payload, setPayload, sourceRows, setSourceRows, config, setConfig: setConfigSafe,
    overrides, setOverrides: setOverridesSafe, loading, error, setError,
    message, setMessage, saving, fetchReport, persistReport, hasUnsavedChanges,
  } = useIncomeReportEditor(id)
  const [pendingDestination, setPendingDestination] = useState(null)
  const [navigationError, setNavigationError] = useState('')
  const [lastAutoSaveAt, setLastAutoSaveAt] = useState(null)
  const [refreshing, setRefreshing] = useState(false)
  const [grouping, setGrouping] = useState(() => parseGrouping(searchParams.get('grouping')))
  const [mainSort, setMainSort] = useState(() => parseMainSort(searchParams.get('mainSort')))
  const [showCols, setShowCols] = useState({
    maxOther: false,
    realOther: false,
    cash: false,
    cashRounded: true,
    otherRounded: true,
    cashPotential: false,
  })
  const [sort, setSort] = useState(() => parseColumnSort(searchParams))
  const [archiveOpen, setArchiveOpen] = useState(false)
  const [archiving, setArchiving] = useState(false)
  const [archiveError, setArchiveError] = useState('')
  const [payoutBusy, setPayoutBusy] = useState(false)
  const [primaryOpen, setPrimaryOpen] = useState(false)
  const [primaryBusy, setPrimaryBusy] = useState(false)
  const [primaryError, setPrimaryError] = useState('')

  const report = payload?.report
  const readOnly = !payload?.can_edit
  const payoutStatus = report?.payout_collection_status || 'none'
  const payoutCollecting = payoutStatus === 'collecting'

  const canArchive = user?.permissions?.['reports.archive'] === true

  const updateViewParams = (patch) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      Object.entries(patch).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') {
          next.delete(key)
        } else {
          next.set(key, value)
        }
      })
      return next
    }, { replace: true })
  }

  const exportCsv = () => {
    const period = report?.period_label ? report.period_label.replace(/\s+/g, '-') : 'report'
    downloadCsv(`income-report-${period}.csv`, buildIncomeReportCsv(result, config.acts_snapshot, config))
  }

  const setGroupingWithQuery = (value) => {
    const nextValue = parseGrouping(value)
    setGrouping(nextValue)
    updateViewParams({ grouping: nextValue })
  }

  const setMainSortWithQuery = (value) => {
    const nextValue = parseMainSort(value)
    setMainSort(nextValue)
    setSort(null)
    updateViewParams({ mainSort: nextValue, sortColumn: null, sortDirection: null })
  }

  const setSortWithQuery = (nextSort) => {
    setSort(nextSort)
    updateViewParams({
      sortColumn: nextSort?.column || null,
      sortDirection: nextSort?.direction || null,
    })
  }

  usePageHeader({
    title: null,
    backLink: '/company/reports',
    backLabel: '← Отчетность',
    renderActions: () => report ? (
      <>
        {!readOnly && (
          <div className="report-save-action">
            <span className="report-save-action__auto">{formatAutoSaveLabel(lastAutoSaveAt)}</span>
            <Button variant="primary" onClick={saveReport} disabled={saving}>
              {saving ? 'Сохранение…' : 'Сохранить'}
            </Button>
          </div>
        )}
      </>
    ) : null,
  }, [report?.id, report?.title, readOnly, canArchive, saving, lastAutoSaveAt])

  const result = useMemo(() => calculateIncomeReport({ sourceRows, config, overrides }), [sourceRows, config, overrides])

  const requestNavigation = (destination) => {
    if (hasUnsavedChanges()) {
      setNavigationError('')
      setPendingDestination(destination)
    } else navigate(destination)
  }

  const saveAndContinue = async () => {
    if (saving) return
    const destination = pendingDestination
    setNavigationError('')
    if (hasUnsavedChanges()) {
      const saved = await persistReport()
      if (!saved) return
      if (hasUnsavedChanges()) {
        setNavigationError('Во время сохранения появились новые правки. Сохраните их перед переходом.')
        return
      }
    }
    setPendingDestination(null)
    navigate(destination)
  }

  const saveReport = async () => {
    await persistReport()
  }

  useEffect(() => {
    if (readOnly || !report?.id) return undefined
    setLastAutoSaveAt(null)
    const intervalId = window.setInterval(() => {
      persistReport({ silent: true }).then((saved) => {
        if (saved) setLastAutoSaveAt(new Date(Date.now()))
      })
    }, AUTO_SAVE_INTERVAL_MS)
    return () => window.clearInterval(intervalId)
  }, [readOnly, report?.id, id])

  const refreshSource = async () => {
    if (readOnly) return
    setRefreshing(true)
    setError('')
    try {
      const response = await api.post(`/company/reports/${id}/refresh-source`)
      setSourceRows(response.data?.source_rows || [])
      await fetchReport()
    } catch (err) {
      setError(err.response?.data?.error || 'Не удалось обновить снимок данных')
    } finally {
      setRefreshing(false)
    }
  }

  const startPayoutWishes = async () => {
    if (readOnly) return
    setPayoutBusy(true)
    setError('')
    setMessage({ type: '', text: '' })
    try {
      await api.post(`/company/reports/${id}/payout-wishes/start`)
      await fetchReport()
      setMessage({ type: 'success', text: 'Сбор пожеланий по выплатам запущен' })
    } catch (err) {
      setError(err.response?.data?.error || 'Не удалось запустить сбор пожеланий')
    } finally {
      setPayoutBusy(false)
    }
  }

  const finishPayoutWishes = async () => {
    if (readOnly) return
    setPayoutBusy(true)
    setError('')
    setMessage({ type: '', text: '' })
    try {
      await api.post(`/company/reports/${id}/payout-wishes/finish`)
      await fetchReport()
      setMessage({ type: 'success', text: 'Сбор пожеланий по выплатам завершён' })
    } catch (err) {
      setError(err.response?.data?.error || 'Не удалось завершить сбор пожеланий')
    } finally {
      setPayoutBusy(false)
    }
  }

  const archiveReport = async () => {
    if (!report?.id) return
    setArchiving(true)
    setArchiveError('')
    try {
      await api.patch(`/company/reports/${report.id}/archive`)
      setArchiveOpen(false)
      await fetchReport()
      setMessage({ type: 'success', text: 'Отчет архивирован' })
    } catch (err) {
      setArchiveError(err.response?.data?.error || 'Не удалось архивировать отчет')
    } finally {
      setArchiving(false)
    }
  }

  const makePrimary = async () => {
    if (readOnly || primaryBusy) return
    setPrimaryBusy(true)
    setPrimaryError('')
    try {
      const response = await api.post(`/company/reports/${id}/make-primary`, {
        expected_primary_id: payload?.primary_report?.id || null,
      })
      const updated = response.data.report
      // Keep unsaved table/config edits: promotion changes metadata only.
      setPayload((previous) => ({ ...previous, report: updated, primary_report: updated }))
      setPrimaryOpen(false)
      setMessage({ type: 'success', text: 'Отчёт назначен основным' })
    } catch (err) {
      setPrimaryError(err.response?.data?.error || 'Не удалось назначить основной отчёт')
      if (err.response?.data && 'primary_report' in err.response.data) {
        setPayload((previous) => ({ ...previous, primary_report: err.response.data.primary_report }))
      }
    } finally {
      setPrimaryBusy(false)
    }
  }

  const saveReportTitle = async (title) => {
    const response = await api.patch(`/company/reports/${id}`, { title })
    const updatedReport = response.data?.report || { ...report, title }
    setPayload((previous) => ({ ...previous, report: updatedReport }))
  }

  if (loading) return <ReportLoadingState />
  if (error && !payload) return <ReportErrorState message={error} onRetry={fetchReport} />

  return (
    <>
      <ReportPageShell>
      <div className="report-toolbar">
        <div className="report-toolbar__meta">
          <div className="report-title-with-status">
            <EditableReportTitle report={report} readOnly={readOnly} onSave={saveReportTitle} />
            <ReportStatusBadge report={report} />
          </div>
          <span>{report?.period_label || '—'}</span>
        </div>
        <div className="report-toolbar__actions">
          <Button variant="secondary" onClick={() => requestNavigation(`/company/reports/${report?.id}/config${location.search}`)}>
            <Icon name="settings" size={16} /> Конфигурация
          </Button>
          <ActionMenu
            label="Действия"
            items={[
              !readOnly && !report?.archived_at && !report?.is_primary && {
                id: 'make-primary',
                label: 'Сделать основным',
                icon: <Icon name="check" size={16} />,
                onSelect: () => { setPrimaryError(''); setPrimaryOpen(true) },
              },
              {
                id: 'csv',
                label: 'CSV',
                icon: <Icon name="download" size={16} />,
                onSelect: exportCsv,
              },
              {
                id: 'pdf',
                label: 'PDF',
                icon: <Icon name="download" size={16} />,
                onSelect: () => window.print(),
              },
              !readOnly && {
                id: 'refresh',
                label: refreshing ? 'Обновляю…' : 'Обновить из актов',
                icon: <Icon name="refresh" size={16} />,
                disabled: refreshing,
                onSelect: refreshSource,
              },
              {
                id: 'accountant',
                label: 'Сформировать отчёт',
                icon: <Icon name="fileCheck" size={16} />,
                onSelect: () => requestNavigation(`/company/reports/${report?.id}/accountant`),
              },
              !readOnly && { type: 'divider', id: 'payout-divider' },
              !readOnly && (payoutCollecting
                ? {
                  id: 'payout-finish',
                  label: 'Завершить сбор пожеланий по выплатам',
                  icon: <Icon name="check" size={16} />,
                  tone: 'success',
                  disabled: payoutBusy,
                  onSelect: finishPayoutWishes,
                }
                : {
                  id: 'payout-start',
                  label: 'Запросить пожелания по выплатам',
                  icon: <Icon name="mail" size={16} />,
                  disabled: payoutBusy,
                  onSelect: startPayoutWishes,
                }),
              !readOnly && canArchive && { type: 'divider', id: 'archive-divider' },
              !readOnly && canArchive && {
                id: 'archive',
                label: 'Архивировать',
                icon: <Icon name="trash" size={16} />,
                tone: 'danger',
                onSelect: () => setArchiveOpen(true),
              },
            ]}
          />
        </div>
      </div>

      {readOnly && (
        <div className="report-readonly-note">
          Режим просмотра: у вашей роли нет права редактировать этот отчет.
        </div>
      )}
      {message.text && <div className={`message message-${message.type} reports-message`} role="alert">{message.text}</div>}
      {error && <div className="message message-error reports-message">{error}</div>}

      <ReportDataTable
        result={result}
        overrides={overrides}
        onOverridesChange={setOverridesSafe}
        readOnly={readOnly}
        grouping={grouping}
        onGroupingChange={setGroupingWithQuery}
        mainSort={mainSort}
        onMainSortChange={setMainSortWithQuery}
        showCols={showCols}
        onShowColsChange={setShowCols}
        sort={sort}
        onSortChange={setSortWithQuery}
        summary={(
          <ReportPnlSummary
            result={result}
            config={config}
            onConfigChange={setConfigSafe}
            readOnly={readOnly}
          />
        )}
      />

      <ReportInfoCards result={result} />
      <IncomeReportCash config={config} onConfigChange={setConfigSafe} readOnly={readOnly} />
      <IncomeReportActs acts={config.acts_snapshot} periodLabel={report?.period_label} />

      {pendingDestination && (
        <UnsavedReportChangesModal
          saving={saving}
          error={navigationError || error}
          onSave={saveAndContinue}
          onStay={() => { if (!saving) setPendingDestination(null) }}
          onLeave={() => { if (!saving) navigate(pendingDestination) }}
        />
      )}
      {primaryOpen && (
        <ConfirmModal
          title="Сделать отчёт основным?"
          message={(
            <>
              <p>Основным за {report?.period_label} станет «{reportTitle(report)}».</p>
              {payload?.primary_report && <p>Отчёт «{reportTitle(payload.primary_report)}» станет черновиком.</p>}
              {payload?.primary_report?.has_ndfl_calculation && <p>По прежнему основному уже сформирован файл для бухгалтера. Следующие кварталы будут считаться от нового основного отчёта; их расчёты потребуется обновить.</p>}
            </>
          )}
          confirmLabel={primaryBusy ? 'Сохранение…' : 'Сделать основным'}
          confirmVariant="primary"
          confirmDisabled={primaryBusy}
          error={primaryError}
          onCancel={() => { if (!primaryBusy) setPrimaryOpen(false) }}
          onConfirm={makePrimary}
        />
      )}
      {archiveOpen && (
        <ConfirmModal
          title="Архивировать отчет?"
          message={<p>Отчет «{reportTitle(report)}» станет доступен только в базе данных.</p>}
          confirmLabel={archiving ? 'Архивация…' : 'Архивировать'}
          confirmVariant="danger"
          confirmDisabled={archiving}
          error={archiveError}
          onCancel={() => {
            setArchiveOpen(false)
            setArchiveError('')
          }}
          onConfirm={archiveReport}
        />
      )}
      </ReportPageShell>
      <EntityComments entityType="reports" entityId={report?.id || id} />
    </>
  )
}

export default IncomeReportPage
